import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Sheet, SheetContent } from './ui/sheet';
import { Button } from './ui/button';
import {
  Mail, MessageCircle, Phone, Camera, Upload, X, User as UserIcon,
  GraduationCap, MapPin, Clock, BookOpen, Loader2, Users
} from 'lucide-react';

// Feature flag — flip to true once a real dialer/softphone integration exists.
// When false the Call button is completely hidden per spec.
const DIALER_ENABLED = (process.env.REACT_APP_DIALER_ENABLED === 'true');

/**
 * Student Profile Card — right-side drawer opened by clicking a student row
 * in the Admin > Students table. Header with CodeITT logo + student photo,
 * clean two-column details, smart contact actions, and full picture-edit UX
 * (webcam capture + file upload → auto crop-to-square + compress → sync).
 */
export default function StudentProfileCard({ open, onClose, studentId, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/students/${studentId}/card`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load student');
    } finally {
      setLoading(false);
    }
  }, [studentId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const photoSrc = data?.profile_photo
    ? (data.profile_photo.startsWith('/api/uploads/') ? `${backend}${data.profile_photo}?auth=${token}` : `${backend}${data.profile_photo}`)
    : null;

  const onPhotoUpdated = async (payload) => {
    try {
      const res = await api.post(`/admin/students/${studentId}/profile-photo`, payload);
      toast.success('Profile photo updated everywhere');
      setEditingPhoto(false);
      // Optimistic + refetch
      setData(d => d ? { ...d, profile_photo: res.data.profile_photo } : d);
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-y-auto" data-testid="student-profile-card">
        {loading || !data ? (
          <SkeletonView />
        ) : (
          <>
            {/* Header */}
            <div className="p-5 border-b border-slate-200 bg-white sticky top-0 z-10">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xs">CA</div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold">CodeITT Academy</p>
                    <h2 className="text-sm font-bold text-slate-900">Student Profile</h2>
                  </div>
                </div>
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="close-profile-card"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setEditingPhoto(true)}
                  className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-blue-500 bg-slate-100 hover:opacity-90 transition group"
                  data-testid="profile-photo"
                  title="Click to change photo"
                >
                  {photoSrc ? (
                    <img src={photoSrc} alt={data.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600 font-bold text-lg">
                      {(data.name || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                    <Camera className="w-4 h-4 text-white" />
                  </div>
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold text-slate-900 truncate">{data.name || '—'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {data.citt_id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">{data.citt_id}</span>}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${data.enrollment_status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {data.enrollment_status || 'Active'}
                    </span>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditingPhoto(true)} className="text-xs" data-testid="upload-photo-btn">
                  <Upload className="w-3 h-3 mr-1" /> Photo
                </Button>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 space-y-5">
              {/* Student Details */}
              <Section title="Student Details">
                <Field icon={GraduationCap} label="Grade" value={data.grade} />
                <Field icon={MapPin} label="Region" value={data.region} />
                <Field icon={Clock} label="Timezone" value={data.timezone} />
              </Section>

              {/* Enrollments — per-vertical with assigned teacher chip */}
              <Section title="Enrollments & Teachers">
                {(data.verticals || []).length === 0 && (
                  <p className="text-xs text-slate-400 italic py-1">No enrollments yet.</p>
                )}
                <div className="flex flex-col gap-2" data-testid="profile-verticals">
                  {(data.verticals || []).map((v, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200" data-testid={`profile-vertical-${i}`}>
                      <BookOpen className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                      <span className="font-bold text-slate-900 text-sm truncate flex-1">{v.vertical || v}</span>
                      {v.teacher_name ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-indigo-100 to-violet-100 border border-indigo-200 text-indigo-800 text-[11px] font-bold"
                          title={v.teacher_citt_id ? `${v.teacher_name} · ${v.teacher_citt_id}` : v.teacher_name}
                          data-testid={`profile-vertical-teacher-${i}`}
                        >
                          <UserIcon className="w-3 h-3" />
                          {v.teacher_name}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">unassigned</span>
                      )}
                    </div>
                  ))}
                </div>
              </Section>

              {/* Contact Details */}
              <Section title="Contact Details">
                <Field icon={Users} label="Parent Name 1" value={data.parent1_name} />
                <Field icon={Users} label="Parent Name 2" value={data.parent2_name} />

                {/* Login username (read-only) */}
                {data.username && (
                  <div className="flex items-center gap-2 text-sm py-1" data-testid="profile-username">
                    <UserIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-slate-500 font-semibold w-28 flex-shrink-0">Login</span>
                    <span className="font-mono font-bold text-slate-800 truncate">{data.username}</span>
                  </div>
                )}

                {/* Smart Email */}
                <ActionRow
                  icon={Mail}
                  label="Email"
                  value={data.email}
                  action={data.email ? (
                    <a href={`mailto:${data.email}`} className="ml-auto text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold" data-testid="action-email">
                      Send
                    </a>
                  ) : null}
                />

                {/* Smart Mobile + Call */}
                <ActionRow
                  icon={Phone}
                  label="Mobile"
                  value={data.mobile}
                  action={data.mobile && DIALER_ENABLED ? (
                    <a href={`tel:${data.mobile}`} className="ml-auto text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold" data-testid="action-call">
                      Call
                    </a>
                  ) : null}
                />

                {/* Smart WhatsApp */}
                <ActionRow
                  icon={MessageCircle}
                  label="WhatsApp"
                  value={data.whatsapp || data.mobile}
                  action={(data.whatsapp || data.mobile) ? (
                    <button
                      type="button"
                      onClick={() => openWhatsApp(data.whatsapp || data.mobile)}
                      className="ml-auto text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold"
                      data-testid="action-whatsapp"
                    >
                      Chat
                    </button>
                  ) : null}
                />
              </Section>

              {/* Future-ready fields — only render when data is present */}
              {(data.next_class || data.classes_completed > 0 || data.upcoming_scheduled > 0) && (
                <Section title="Schedule & Progress">
                  {data.next_class && (
                    <Field icon={Clock} label="Next Class"
                      value={`${data.next_class.date} · ${data.next_class.start_time} · ${data.next_class.vertical} (${data.next_class.teacher_name || 'TBD'})`} />
                  )}
                  {data.classes_completed > 0 && <Field icon={GraduationCap} label="Classes Completed" value={String(data.classes_completed)} />}
                  {data.upcoming_scheduled > 0 && <Field icon={BookOpen} label="Upcoming Scheduled" value={String(data.upcoming_scheduled)} />}
                </Section>
              )}
            </div>

            {editingPhoto && (
              <PhotoEditor
                onClose={() => setEditingPhoto(false)}
                onSubmit={onPhotoUpdated}
              />
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ────────── Sub-components ────────── */

const Section = ({ title, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-2">{title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
    <span className="text-xs text-slate-500 font-semibold w-28 flex-shrink-0">{label}</span>
    <span className="text-sm text-slate-800 font-medium truncate">{value || <em className="text-slate-300">—</em>}</span>
  </div>
);

const ActionRow = ({ icon: Icon, label, value, action }) => (
  <div className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
    <span className="text-xs text-slate-500 font-semibold w-28 flex-shrink-0">{label}</span>
    <span className="text-sm text-slate-800 font-medium truncate flex-1">{value || <em className="text-slate-300">—</em>}</span>
    {action}
  </div>
);

const SkeletonView = () => (
  <div className="p-6 space-y-4 animate-pulse" data-testid="profile-skeleton">
    <div className="flex items-center gap-3">
      <div className="w-16 h-16 rounded-full bg-slate-200" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-slate-200 rounded w-2/3" />
        <div className="h-3 bg-slate-100 rounded w-1/3" />
      </div>
    </div>
    <div className="space-y-2">
      {[...Array(6)].map((_, i) => <div key={i} className="h-6 bg-slate-100 rounded" />)}
    </div>
  </div>
);

/** WhatsApp: try native app first, fall back to WhatsApp Web after 400 ms. */
function openWhatsApp(rawNumber) {
  const num = String(rawNumber || '').replace(/[^\d]/g, '');
  if (!num) return;
  const native = `whatsapp://send?phone=${num}`;
  const web = `https://wa.me/${num}`;
  // Best-effort dual open
  let webOpened = false;
  const openWeb = () => { if (!webOpened) { webOpened = true; window.open(web, '_blank'); } };
  try {
    window.location.href = native;
  } catch { /* ignore */ }
  setTimeout(openWeb, 500);
}

/* ────────── Photo editor (Camera + Upload → crop to square + compress) ────────── */

function PhotoEditor({ onClose, onSubmit }) {
  const [mode, setMode] = useState('choose');    // 'choose' | 'camera' | 'preview'
  const [preview, setPreview] = useState(null);   // {dataUrl, blob}
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; }
      setMode('camera');
    } catch (e) {
      toast.error('Camera unavailable. Please upload from file instead.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };
  useEffect(() => () => stopCamera(), []);

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video) return;
    const size = Math.min(video.videoWidth, video.videoHeight);
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    canvas.getContext('2d').drawImage(video, sx, sy, size, size, 0, 0, 512, 512);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setPreview({ dataUrl });
    stopCamera();
    setMode('preview');
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
      toast.error('Only JPG, PNG, or WEBP allowed');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Max 5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Crop to square around centre, resize to 512x512, jpeg quality 0.85
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        canvas.getContext('2d').drawImage(img, sx, sy, size, size, 0, 0, 512, 512);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setPreview({ dataUrl });
        setMode('preview');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!preview?.dataUrl) return;
    setBusy(true);
    try {
      await onSubmit({ image_base64: preview.dataUrl, content_type: 'image/jpeg' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-editor">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Update Profile Photo</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>

        {mode === 'choose' && (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={startCamera}
              className="p-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 transition text-center"
              data-testid="photo-camera-btn"
            >
              <Camera className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-bold text-slate-800">Use Camera</p>
              <p className="text-[10px] text-slate-500">Capture from webcam</p>
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 transition text-center"
              data-testid="photo-upload-btn"
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <p className="text-sm font-bold text-slate-800">Upload File</p>
              <p className="text-[10px] text-slate-500">JPG/PNG/WEBP · max 5 MB</p>
            </button>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="photo-file-input" />
          </div>
        )}

        {mode === 'camera' && (
          <div className="space-y-3">
            <video ref={videoRef} autoPlay playsInline className="w-full aspect-square rounded-lg bg-black object-cover" data-testid="camera-preview" />
            <div className="flex gap-2">
              <Button onClick={capturePhoto} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="capture-btn">
                <Camera className="w-4 h-4 mr-1" /> Capture
              </Button>
              <Button variant="outline" onClick={() => { stopCamera(); setMode('choose'); }}>Cancel</Button>
            </div>
          </div>
        )}

        {mode === 'preview' && preview && (
          <div className="space-y-3">
            <img src={preview.dataUrl} alt="preview" className="w-full aspect-square object-cover rounded-lg border border-slate-200" data-testid="photo-preview" />
            <div className="flex gap-2">
              <Button onClick={submit} disabled={busy} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="photo-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading…</> : 'Save Photo'}
              </Button>
              <Button variant="outline" onClick={() => { setPreview(null); setMode('choose'); }} data-testid="photo-retake">Retake</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
